import type {AutosaveConfig, Logger, PS} from '@wix/document-services-types'
import * as santaCoreUtils from '@wix/santa-core-utils'
import _ from 'lodash'
import hooks from '../hooks/hooks'
import type {HookFunc} from '../hooks/hooksHandlerFactory'
import pageData from '../page/pageData'
import type {OnSuccess} from '../saveAPI/createSaveAPI'
import saveAPI from '../saveAPI/saveAPI'
import type {DistributorExtensionAPI, TriggerCreateRevision} from '@wix/document-manager-extensions'

const CSAVE_ACTIONS_THRESHOLD = 10_000
const UNAPPLIED_ACTIONS_THRESHOLD = 20000
const UNAPPLIED_TRANSACTIONS_THRESHOLD = 300

interface AutosaveDebounced {
    (ps: PS, s: OnSuccess, e: (e: any, t?: string) => void, trigger: string): void
    cancel(): void
}

let performAutosaveDebounced: AutosaveDebounced
let autosaveTimeoutId: any // timeout id

const defaultConfig = {
    AUTOSAVE_ACTION_COUNT: 2,
    SAVE_AFTER_AUTOSAVE_COUNT: 3,
    DEBOUNCE_WAIT: 2,
    AUTOSAVE_TIMEOUT: 10
}

let config: {
    enabled?: boolean
    AUTOSAVE_TIMEOUT?: number
    SAVE_AFTER_AUTOSAVE_COUNT?: number
    AUTOSAVE_ACTION_COUNT?: number
    DEBOUNCE_WAIT?: number
} = {}

const innerPaths = {
    SHOULD_AUTOSAVE: 'shouldAutoSave',
    ACTIONS_COUNT: 'actionsCount',
    AUTOSAVES_COUNT: 'autosaveCount',
    AUTO_FULL_SAVE_FLAG: 'autoFullSaveFlag',
    REPORTING_INFO: 'reportingInfo'
}
const counterOps = {
    INCREASE: 'increase',
    DECREASE: 'decrease',
    RESET: 'reset'
}
const INIT_AUTOSAVE_INTERACTION = 'autosave-init'
const {HOOKS} = hooks
let hooksMap = {}

function autosaveDebouncedWithDefaultCallbacks(ps: PS, trigger: string) {
    performAutosaveDebounced(ps, _.noop, _.noop, trigger)
}

function handleUserAction(ps: PS) {
    if (!canSave(ps)) {
        return
    }
    performAutosaveDebounced = performAutosaveDebounced || _.debounce(performAutosave, config.DEBOUNCE_WAIT * 1000)
    setAutosaveTimeout(ps)
    const newCount = increaseCounter(ps, innerPaths.ACTIONS_COUNT)
    if (newCount >= config.AUTOSAVE_ACTION_COUNT) {
        autosaveDebouncedWithDefaultCallbacks(ps, 'num of actions')
    }
}

function performAutosave(ps: PS, onSuccess: OnSuccess, onError: (e: any, t?: string) => void, trigger: string) {
    const logger = ps.extensionAPI.logger as Logger
    const autosaveCount = increaseCounter(ps, innerPaths.AUTOSAVES_COUNT)
    const autoSavesThresholdPassed = autosaveCount >= config.SAVE_AFTER_AUTOSAVE_COUNT
    const unappliedTransactionCountSinceLastRevision =
        ps.extensionAPI.continuousSave.getUnappliedTransactionCountSinceLastRevision()
    const unappliedTransactionThresholdPassed = ps.extensionAPI.continuousSave.isUnappliedTransactionThresholdPassed()
    const storeActionsCountSinceLastCsaveSnapshot = ps.extensionAPI.continuousSave.getActionCount()
    const isActionCountThresholdPassed = storeActionsCountSinceLastCsaveSnapshot > CSAVE_ACTIONS_THRESHOLD
    const unappliedActionCountSinceLastRevisionWithoutCSave =
        ps.extensionAPI.continuousSave.getUnappliedActionCountSinceLastRevision()
    const isUnappliedActionsThresholdPassed =
        unappliedActionCountSinceLastRevisionWithoutCSave > UNAPPLIED_ACTIONS_THRESHOLD
    const isUnappliedTransactionThresholdPassed =
        unappliedTransactionCountSinceLastRevision > UNAPPLIED_TRANSACTIONS_THRESHOLD

    const forcePartialSave =
        isUnappliedTransactionThresholdPassed ||
        autoSavesThresholdPassed ||
        unappliedTransactionThresholdPassed ||
        isActionCountThresholdPassed ||
        isUnappliedActionsThresholdPassed
    clear(ps)

    const unappliedTransData = ps.extensionAPI.continuousSave.getUnappliedTransactionsData()
    logger.interactionStarted('performAutosave', {
        extras: {
            unappliedTransactionCountSinceLastRevision,
            getUnappliedTransactionsData: JSON.stringify(unappliedTransData),
            unappliedActionCountSinceLastRevisionWithoutCSave,
            storeActionsCountSinceLastCsaveSnapshot,
            estimatedUnappliedActionCount:
                unappliedActionCountSinceLastRevisionWithoutCSave + storeActionsCountSinceLastCsaveSnapshot
        }
    })

    if (!forcePartialSave) {
        const reportingInfoPointer = getAutoSaveInnerPointer(ps, innerPaths.REPORTING_INFO)

        ps.dal.set(reportingInfoPointer, {
            actionsPerAutosave: config.AUTOSAVE_ACTION_COUNT,
            autosavesPerFull: config.SAVE_AFTER_AUTOSAVE_COUNT,
            pagesCount: pageData.getNumberOfPages(ps),
            trigger
        })

        // eslint-disable-next-line promise/prefer-await-to-then
        ps.extensionAPI.continuousSave.save().then(onSuccess, (e: any) => {
            onError(e, trigger)
        })
    } else {
        logger.interactionStarted('forcePartialUpdate', {
            extras: {
                tags: {
                    autoSavesThresholdPassed,
                    unappliedTransactionThresholdPassed,
                    isActionCountThresholdPassed
                },
                isUnappliedTransactionThresholdPassed,
                autoSavesThresholdPassed,
                unappliedTransactionThresholdPassed,
                newMechanism: true
            }
        })
        const onPartialSaveSuccess = getPartialSaveCompletedCallback(ps, trigger, onSuccess, true)
        const onPartialSaveFail = getPartialSaveCompletedCallback(ps, trigger, onError, false)
        const autoFullSaveFlagPointer = getAutoSaveInnerPointer(ps, innerPaths.AUTO_FULL_SAVE_FLAG)
        ps.dal.set(autoFullSaveFlagPointer, true)
        _.invoke(config, 'onPartialSaveStarted', trigger)
        const isFullSave = false
        saveAPI.save(ps, onPartialSaveSuccess as any, onPartialSaveFail, isFullSave, {origin: 'autosave'})
    }

    logger.interactionEnded('performAutosave', {})
}

async function partialSave(ps: PS, trigger: string) {
    const onFinish = (e?: any) => {
        ps.dal.remove(autoFullSaveFlagPointer)
        _.invoke(config, 'onPartialSaveFinished', e, trigger)
        resetCounter(ps, innerPaths.AUTOSAVES_COUNT)
    }
    const autoFullSaveFlagPointer = getAutoSaveInnerPointer(ps, innerPaths.AUTO_FULL_SAVE_FLAG)
    ps.dal.set(autoFullSaveFlagPointer, true)
    _.invoke(config, 'onPartialSaveStarted', trigger)
    try {
        await saveAPI.promises.save(ps, false, {trigger})
        onFinish()
    } catch (e) {
        onFinish(e)
    }
}

function getPartialSaveCompletedCallback(
    ps: PS,
    trigger: string,
    callback: (error: any, trigger: string) => void,
    success: boolean
) {
    return function (error: any) {
        ps.extensionAPI.logger.interactionEnded('forcePartialUpdate', {extras: {success}})
        const autoFullSaveFlagPointer = getAutoSaveInnerPointer(ps, innerPaths.AUTO_FULL_SAVE_FLAG)
        ps.dal.remove(autoFullSaveFlagPointer)
        _.invoke(config, 'onPartialSaveFinished', error, trigger)
        resetCounter(ps, innerPaths.AUTOSAVES_COUNT)
        if (_.isFunction(callback)) {
            callback(error, trigger)
        }
    }
}

function handleValidationError(ps: PS) {
    disableAutosave(ps)
}

function disableAutosave(ps: PS) {
    init(ps, {enabled: false})
}

function setAutosaveTimeout(ps: PS) {
    clearAutosaveTimeout()
    autosaveTimeoutId = setTimeout(() => {
        autosaveDebouncedWithDefaultCallbacks(ps, 'idle')
    }, config.AUTOSAVE_TIMEOUT * 1000)
}

function clearAutosaveTimeout() {
    if (autosaveTimeoutId) {
        clearTimeout(autosaveTimeoutId)
        autosaveTimeoutId = null
    }
    if (performAutosaveDebounced) {
        performAutosaveDebounced.cancel()
    }
}

function updateCounter(ps: PS, key: string, operation: string) {
    const counterPointer = getAutoSaveInnerPointer(ps, key)
    const currentCount = ps.dal.get(counterPointer) || 0
    let newCount

    switch (operation) {
        case counterOps.INCREASE:
            newCount = currentCount + 1
            break
        case counterOps.DECREASE:
            newCount = currentCount - 1
            break
        case counterOps.RESET:
            newCount = 0
            break
    }

    ps.dal.set(counterPointer, newCount)

    return newCount
}

function increaseCounter(ps: PS, key: string) {
    return updateCounter(ps, key, counterOps.INCREASE)
}

function resetCounter(ps: PS, key: string) {
    return updateCounter(ps, key, counterOps.RESET)
}

function clear(ps: PS) {
    clearAutosaveTimeout()
    resetCounter(ps, innerPaths.ACTIONS_COUNT)
}

function clearAll(ps: PS) {
    clear(ps)
    resetCounter(ps, innerPaths.AUTOSAVES_COUNT)
}

function getAutoSaveInnerPointer(ps: PS, key: string) {
    return ps.pointers.general.getAutoSaveInnerPointer(key)
}

const registerHook = (name: string, hook: HookFunc) => {
    hooksMap[name] = hooks.registerHook(name, hook)
}

function registerHooks() {
    registerHook(HOOKS.AUTOSAVE.ACTION, handleUserAction)
    registerHook(HOOKS.SAVE.SITE_SAVED, clearAll)
    registerHook(HOOKS.SAVE.VALIDATION_ERROR, handleValidationError)
}

function unregisterHooks() {
    _.forEach(hooksMap, (hookValue, hookKey) => hooks.unregisterHook(hookKey, hookValue))
    hooksMap = {}
}

function isAutoSaveAllowed(ps: PS) {
    const autoSaveInfo = getAutoSaveInfo(ps)
    return autoSaveInfo?.shouldAutoSave && !ps.config.disableAutoSave
}

function canSave(ps: PS) {
    return config.enabled !== false && !!(isAutoSaveAllowed(ps) && saveAPI.saveState.canSave(ps))
}

function getAutoSaveInfo(ps: PS) {
    const autosaveInfoPointer = ps.pointers.general.getAutosaveInfo()
    return ps.dal.get(autosaveInfoPointer)
}

const saveAfterNonRecoverableError = (ps: PS) => partialSave(ps, HOOKS.AUTOSAVE.CSAVE_NON_RECOVERABLE_ERROR)

function init(ps: PS, _config: AutosaveConfig) {
    if (!_config) {
        santaCoreUtils.log.error(new Error('Missing autosave config object'))
        return false
    }

    const wrappedHooks = ps.extensionAPI.continuousSave.getWrappedHooks(_config)
    config = _.defaults(wrappedHooks, _config, defaultConfig)
    ps.extensionAPI.logger.interactionStarted(INIT_AUTOSAVE_INTERACTION, {tags: {enable: config.enabled}})

    unregisterHooks()

    ps.extensionAPI.continuousSave.setEnabled(config.enabled)
    ps.extensionAPI.continuousSave.initHooks(config)
    registerHook(HOOKS.AUTOSAVE.CSAVE_NON_RECOVERABLE_ERROR, saveAfterNonRecoverableError)

    clearAutosaveTimeout()

    if (_config.enabled !== true || !saveAPI.saveState.isEnabled(ps)) {
        return false
    }

    registerHooks()
    ps.extensionAPI.logger.interactionEnded(INIT_AUTOSAVE_INTERACTION, {tags: {enable: config.enabled}})
    return true
}

const initMethod = (ps: PS, {disableAutoSave = false} = {disableAutoSave: false}) => {
    if (disableAutoSave) {
        const shouldAutoSavePtr = getAutoSaveInnerPointer(ps, innerPaths.SHOULD_AUTOSAVE)
        ps.dal.set(shouldAutoSavePtr, false)
    } else if (canSave(ps)) {
        performAutosave(ps, _.noop, _.noop, 'init method')
    }
    const {distributor} = ps.extensionAPI as DistributorExtensionAPI
    distributor.subscribeToMessage<TriggerCreateRevision>(
        'CONCURRENT_EDITING_SHOULD_TRIGGER_CREATE_REVISION',
        ({id: correlationId}) => {
            if (ps.extensionAPI.continuousSave.isLocalTransaction(correlationId)) {
                const onPartialSaveSuccess = getPartialSaveCompletedCallback(ps, 'init method', _.noop, true)
                const onPartialSaveFail = getPartialSaveCompletedCallback(ps, 'init method', _.noop, false)
                ps.extensionAPI.logger.interactionStarted('triggerCreateRevision')
                saveAPI.save(ps, onPartialSaveSuccess as any, onPartialSaveFail, false, {origin: 'autosave'})
            }
        }
    )
}

export default {
    initMethod,
    init,
    autosave(ps: PS, onSuccess: OnSuccess, onError: (e: any, t?: string) => void, triggerName?: string) {
        if (!canSave(ps)) {
            return onError({document: 'CSave not available'})
        }
        performAutosave(ps, onSuccess, onError, triggerName)
    },
    canAutosave: canSave,
    getAutoSaveInfo,
    clearAll,
    disableAutosave,
    handleValidationError,
    isAllowed: isAutoSaveAllowed,
    CSAVE_ACTIONS_THRESHOLD
}
